export interface UserRequestHistoryItem {
  request_id: string;
  endpoint: string;
  endpoint_name: string;
  timestamp: string;
  status_code: number;
  client_id: string;
  auth_id: string;
  league_id: string;
  team_name: string;
  league_name: string;
  share_url: string;
  audio_url: string;
  content?: string;
  /** @deprecated Prefer content — kept for older payloads */
  response?: string;
  charts?: Record<string, unknown> | null;
}

export interface UserRequestHistoryResponse {
  items: UserRequestHistoryItem[];
  total: number;
  offset: number;
  limit: number;
  order: string;
  endpoint: string | null;
}

export interface FetchUserRequestHistoryParams {
  offset?: number;
  limit?: number;
  order?: "asc" | "desc";
  /** Filter: e.g. "waivers", "rundown", "start", "trade" — omit for all (except super_analysis) */
  endpoint?: string | null;
  league_id?: string | null;
  integration_name?: string | null;
  /** Platform user id (not auth_id — backend injects auth_id from JWT) */
  user_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

/** Page size for history fetches. Change here to tune all call sites. */
export const USER_REQUEST_HISTORY_PAGE_SIZE = 20;

/**
 * GET /functions/endpoints/user_request_history (ROTO backend → POC).
 * Backend injects auth_id from Auth0 JWT; front only sends league filters + pagination.
 */
export async function fetchUserRequestHistory(
  accessAuthToken: string,
  params: FetchUserRequestHistoryParams = {}
): Promise<UserRequestHistoryResponse> {
  const {
    offset = 0,
    limit = USER_REQUEST_HISTORY_PAGE_SIZE,
    order = "desc",
    endpoint = null,
    league_id = null,
    integration_name = null,
    user_id = null,
    start_date = null,
    end_date = null,
  } = params;

  const query = new URLSearchParams();
  query.set("offset", String(offset));
  query.set("limit", String(limit));
  query.set("order", order);
  if (endpoint) query.set("endpoint", endpoint);
  if (league_id) query.set("league_id", league_id);
  if (integration_name) query.set("integration_name", integration_name);
  if (user_id) query.set("user_id", user_id);
  if (start_date) query.set("start_date", start_date);
  if (end_date) query.set("end_date", end_date);

  const url = `${process.env.REACT_APP_BACKEND_URL}/functions/endpoints/user_request_history?${query.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessAuthToken}`,
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail =
      (errorData as { detail?: string; message?: string })?.detail ||
      (errorData as { message?: string })?.message;
    throw new Error(detail || `user_request_history failed (${response.status})`);
  }

  return response.json() as Promise<UserRequestHistoryResponse>;
}
